/* eslint-disable no-unused-expressions */
/* eslint-disable max-len */
import { Button, Box } from '@mui/material';
import { setPlayerTeam, addPlayerToTeam, resetAllTeamsStats } from 'redux/slices';
import { useSelector, useDispatch } from 'react-redux';

const getPlayerPoints = (p) => p.totalPoints ?? (p.wins - p.losses);

const calculateTeamScore = (team) => team.reduce((acc, p) => acc + getPlayerPoints(p), 0);

const findBestSwap = (teamA, teamB, genderBalanceMode, isOddFemales, setterRestrictedMode) => {
  const teamAScore = calculateTeamScore(teamA);
  const teamBScore = calculateTeamScore(teamB);
  const teamAFemales = teamA.filter((p) => p.gender === 'F').length;
  const teamBFemales = teamB.filter((p) => p.gender === 'F').length;
  const teamASetters = teamA.filter((p) => p.playerType === 'Setter').length;
  const teamBSetters = teamB.filter((p) => p.playerType === 'Setter').length;
  const totalSetters = teamASetters + teamBSetters;

  let targetDiff = 0;
  if (isOddFemales && genderBalanceMode) {
    const avgScore = (teamAScore + teamBScore) / 2;
    targetDiff = avgScore * 0.1;
    if (teamAFemales > teamBFemales) {
      targetDiff = teamAScore - teamBScore > 0 ? targetDiff : -targetDiff;
    } else {
      targetDiff = teamBScore - teamAScore > 0 ? targetDiff : -targetDiff;
    }
  }

  const currentDiff = Math.abs(teamAScore - teamBScore - (teamAFemales > teamBFemales ? targetDiff : -targetDiff));

  let bestSwap = null;
  let bestDiff = currentDiff;

  teamA.forEach((playerA) => {
    teamB.forEach((playerB) => {
      if (genderBalanceMode && playerA.gender !== playerB.gender) return;

      if (setterRestrictedMode && totalSetters >= 2) {
        const newTeamASetters = teamASetters - (playerA.playerType === 'Setter' ? 1 : 0) + (playerB.playerType === 'Setter' ? 1 : 0);
        const newTeamBSetters = teamBSetters - (playerB.playerType === 'Setter' ? 1 : 0) + (playerA.playerType === 'Setter' ? 1 : 0);
        if (newTeamASetters === 0 || newTeamBSetters === 0) return;
      }

      const newTeamAScore = teamAScore - getPlayerPoints(playerA) + getPlayerPoints(playerB);
      const newTeamBScore = teamBScore - getPlayerPoints(playerB) + getPlayerPoints(playerA);

      let newDiff;
      if (isOddFemales && genderBalanceMode && teamAFemales > teamBFemales) {
        newDiff = Math.abs(newTeamAScore - newTeamBScore * 1.1);
      } else if (isOddFemales && genderBalanceMode && teamBFemales > teamAFemales) {
        newDiff = Math.abs(newTeamBScore - newTeamAScore * 1.1);
      } else {
        newDiff = Math.abs(newTeamAScore - newTeamBScore);
      }

      if (newDiff < bestDiff) {
        bestDiff = newDiff;
        bestSwap = { playerA, playerB };
      }
    });
  });

  return bestSwap;
};

const distributePlayersWithGenderBalance = (allPlayers, teamSize, genderRestrictedMode) => {
  const females = allPlayers.filter((p) => p.gender === 'F');
  const males = allPlayers.filter((p) => p.gender !== 'F');
  const setters = allPlayers.filter((p) => p.playerType === 'Setter');

  const teamA = [];
  const teamB = [];
  const waiting = [];

  let femalesToUse = [...females];
  let isOddFemales = false;
  let hasSingleFemale = false;

  if (genderRestrictedMode) {
    if (females.length === 1) {
      hasSingleFemale = true;
      isOddFemales = true;
    } else if (females.length % 2 === 1) {
      const sortedFemales = [...females].sort((a, b) => getPlayerPoints(b) - getPlayerPoints(a));
      waiting.push(sortedFemales[sortedFemales.length - 1]);
      femalesToUse = sortedFemales.slice(0, -1);
    }
  } else {
    isOddFemales = females.length % 2 === 1;
  }

  const sortedFemales = [...femalesToUse].sort((a, b) => getPlayerPoints(b) - getPlayerPoints(a));

  for (let i = 0; i < sortedFemales.length; i += 1) {
    if (i % 2 === 0) {
      teamA.push(sortedFemales[i]);
    } else {
      teamB.push(sortedFemales[i]);
    }
  }

  // Verificar se os times já têm setter (pode ser uma levantadora que entrou como mulher)
  const teamAHasSetter = teamA.some((p) => p.playerType === 'Setter');
  const teamBHasSetter = teamB.some((p) => p.playerType === 'Setter');

  // Setters que ainda não foram distribuídos (excluindo levantadoras que já entraram como mulheres)
  const remainingSetters = setters.filter((s) => !teamA.includes(s) && !teamB.includes(s) && !waiting.includes(s));
  const sortedSetters = [...remainingSetters].sort((a, b) => getPlayerPoints(b) - getPlayerPoints(a));

  // Adicionar setters aos times que ainda não têm, respeitando o limite de mulheres no modo restrito
  if (!teamAHasSetter && sortedSetters.length > 0 && teamA.length < teamSize) {
    const setter = sortedSetters.shift();
    // Se o setter é mulher e estamos no modo restrito, verificar se não excede o limite
    const teamAFemaleCount = teamA.filter((p) => p.gender === 'F').length;
    const femalesPerTeamLimit = Math.ceil(femalesToUse.length / 2);
    if (setter.gender === 'F' && genderRestrictedMode && teamAFemaleCount >= femalesPerTeamLimit) {
      // Não adicionar, pois excederia o limite de mulheres
      sortedSetters.unshift(setter);
    } else {
      teamA.push(setter);
    }
  }
  if (!teamBHasSetter && sortedSetters.length > 0 && teamB.length < teamSize) {
    const setter = sortedSetters.shift();
    const teamBFemaleCount = teamB.filter((p) => p.gender === 'F').length;
    const femalesPerTeamLimit = Math.ceil(femalesToUse.length / 2);
    if (setter.gender === 'F' && genderRestrictedMode && teamBFemaleCount >= femalesPerTeamLimit) {
      // Não adicionar, pois excederia o limite de mulheres
      sortedSetters.unshift(setter);
    } else {
      teamB.push(setter);
    }
  }

  // Jogadores restantes: homens que não foram distribuídos + setters que sobraram
  const remainingPlayers = males.filter((p) => !teamA.includes(p) && !teamB.includes(p) && !waiting.includes(p));
  const sortedRemaining = [...remainingPlayers].sort((a, b) => getPlayerPoints(b) - getPlayerPoints(a));

  sortedRemaining.forEach((player) => {
    const teamAScore = calculateTeamScore(teamA);
    const teamBScore = calculateTeamScore(teamB);
    const teamAFemales = teamA.filter((p) => p.gender === 'F').length;
    const teamBFemales = teamB.filter((p) => p.gender === 'F').length;

    if (teamA.length >= teamSize) {
      teamB.push(player);
    } else if (teamB.length >= teamSize) {
      teamA.push(player);
    } else if (hasSingleFemale && teamAFemales > teamBFemales) {
      const targetScoreA = teamBScore * 1.1;
      if (teamAScore < targetScoreA) {
        teamA.push(player);
      } else {
        teamB.push(player);
      }
    } else if (hasSingleFemale && teamBFemales > teamAFemales) {
      const targetScoreB = teamAScore * 1.1;
      if (teamBScore < targetScoreB) {
        teamB.push(player);
      } else {
        teamA.push(player);
      }
    } else if (isOddFemales && !genderRestrictedMode && teamAFemales > teamBFemales) {
      const targetScoreA = teamBScore * 1.1;
      if (teamAScore < targetScoreA) {
        teamA.push(player);
      } else {
        teamB.push(player);
      }
    } else if (isOddFemales && !genderRestrictedMode && teamBFemales > teamAFemales) {
      const targetScoreB = teamAScore * 1.1;
      if (teamBScore < targetScoreB) {
        teamB.push(player);
      } else {
        teamA.push(player);
      }
    } else if (teamAScore <= teamBScore) {
      teamA.push(player);
    } else {
      teamB.push(player);
    }
  });

  return {
    teamA, teamB, isOddFemales: hasSingleFemale, waiting,
  };
};

const distributeSetters = (allPlayers, teamSize, setterRestrictedMode) => {
  const setters = allPlayers.filter((p) => p.playerType === 'Setter');
  const nonSetters = allPlayers.filter((p) => p.playerType !== 'Setter');

  const teamA = [];
  const teamB = [];

  if (setterRestrictedMode && setters.length >= 2) {
    const sortedSetters = [...setters].sort((a, b) => getPlayerPoints(b) - getPlayerPoints(a));
    teamA.push(sortedSetters[0]);
    teamB.push(sortedSetters[1]);
    sortedSetters.slice(2).forEach((s) => nonSetters.push(s));
  } else if (setters.length >= 2) {
    const sortedSetters = [...setters].sort((a, b) => getPlayerPoints(b) - getPlayerPoints(a));
    teamA.push(sortedSetters[0]);
    teamB.push(sortedSetters[1]);
    sortedSetters.slice(2).forEach((s) => nonSetters.push(s));
  } else if (setters.length === 1) {
    teamA.push(setters[0]);
  }

  const sortedNonSetters = [...nonSetters].sort((a, b) => getPlayerPoints(b) - getPlayerPoints(a));

  sortedNonSetters.forEach((player) => {
    if (teamA.length < teamSize && (teamA.length <= teamB.length || teamB.length >= teamSize)) {
      teamA.push(player);
    } else if (teamB.length < teamSize) {
      teamB.push(player);
    }
  });

  return { teamA, teamB, setterRestrictedMode };
};

export function BalanceTeamsBtn() {
  const players = useSelector((state) => state.players);
  const genderBalanceMode = useSelector((state) => state.game.genderBalanceMode);
  const genderRestrictedMode = useSelector((state) => state.game.genderRestrictedMode);
  const setterRestrictedMode = useSelector((state) => state.game.setterRestrictedMode);
  const dispatch = useDispatch();

  const balanceTeams = () => {
    const currentTeamA = players.filter((player) => player.teamId === 1);
    const currentTeamB = players.filter((player) => player.teamId === 2);
    const bothTeams = [...currentTeamA, ...currentTeamB];
    const teamSize = Math.max(currentTeamA.length, currentTeamB.length);

    const distribution = genderBalanceMode
      ? distributePlayersWithGenderBalance(bothTeams, teamSize, genderRestrictedMode)
      : distributeSetters(bothTeams, teamSize, setterRestrictedMode);

    let { teamA, teamB } = distribution;
    const isOddFemales = distribution.isOddFemales || false;
    const waitingPlayers = distribution.waiting || [];

    let improved = true;
    while (improved) {
      const swap = findBestSwap(teamA, teamB, genderBalanceMode, isOddFemales, setterRestrictedMode);
      if (swap) {
        teamA = teamA.filter((p) => p.playerId !== swap.playerA.playerId);
        teamA.push(swap.playerB);
        teamB = teamB.filter((p) => p.playerId !== swap.playerB.playerId);
        teamB.push(swap.playerA);
      } else {
        improved = false;
      }
    }

    const teamASetters = teamA.filter((p) => p.playerType === 'Setter');
    const teamBSetters = teamB.filter((p) => p.playerType === 'Setter');

    if (teamASetters.length > 1 && teamBSetters.length === 0) {
      const setterToMove = teamASetters[teamASetters.length - 1];
      const nonSettersTeamB = teamB.filter((p) => p.playerType !== 'Setter');
      const validCandidates = genderBalanceMode
        ? nonSettersTeamB.filter((p) => p.gender === setterToMove.gender)
        : nonSettersTeamB;

      if (validCandidates.length > 0) {
        const setterScore = getPlayerPoints(setterToMove);
        const bestMatch = [...validCandidates].sort((a, b) => Math.abs(getPlayerPoints(a) - setterScore) - Math.abs(getPlayerPoints(b) - setterScore))[0];

        teamA = teamA.filter((p) => p.playerId !== setterToMove.playerId);
        teamA.push(bestMatch);
        teamB = teamB.filter((p) => p.playerId !== bestMatch.playerId);
        teamB.push(setterToMove);
      }
    } else if (teamBSetters.length > 1 && teamASetters.length === 0) {
      const setterToMove = teamBSetters[teamBSetters.length - 1];
      const nonSettersTeamA = teamA.filter((p) => p.playerType !== 'Setter');
      const validCandidates = genderBalanceMode
        ? nonSettersTeamA.filter((p) => p.gender === setterToMove.gender)
        : nonSettersTeamA;

      if (validCandidates.length > 0) {
        const setterScore = getPlayerPoints(setterToMove);
        const bestMatch = [...validCandidates].sort((a, b) => Math.abs(getPlayerPoints(a) - setterScore) - Math.abs(getPlayerPoints(b) - setterScore))[0];

        teamB = teamB.filter((p) => p.playerId !== setterToMove.playerId);
        teamB.push(bestMatch);
        teamA = teamA.filter((p) => p.playerId !== bestMatch.playerId);
        teamA.push(setterToMove);
      }
    }

    teamA.forEach((player) => {
      dispatch(setPlayerTeam({ playerId: player.playerId, teamId: 1 }));
      dispatch(addPlayerToTeam({ playerId: player.playerId, teamId: 1 }));
    });

    teamB.forEach((player) => {
      dispatch(setPlayerTeam({ playerId: player.playerId, teamId: 2 }));
      dispatch(addPlayerToTeam({ playerId: player.playerId, teamId: 2 }));
    });

    waitingPlayers.forEach((player) => {
      dispatch(setPlayerTeam({ playerId: player.playerId, teamId: 0 }));
    });

    dispatch(resetAllTeamsStats());
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100px',
      }}
    >
      <Button variant="contained" onClick={balanceTeams} size="small">Balancear times</Button>
    </Box>
  );
}
